import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ArtistCreationFormData {
  // Artist Details
  artistName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  ddi?: string;
  ddiCountryCode?: string; // Store country code for flag restoration
  phoneMask?: string; // Store phone mask for input restoration
  phoneNumber?: string;
  birthDate?: string;

  // Artist Access (excluding sensitive password data)
  username?: string;

  // Form state
  currentStep?: string;
}

export interface ArtistFormControlStates {
  // Track which fields have been interacted with
  artistName?: { touched: boolean; dirty: boolean };
  firstName?: { touched: boolean; dirty: boolean };
  lastName?: { touched: boolean; dirty: boolean };
  email?: { touched: boolean; dirty: boolean };
  ddi?: { touched: boolean; dirty: boolean };
  phoneNumber?: { touched: boolean; dirty: boolean };
  birthDate?: { touched: boolean; dirty: boolean };
  username?: { touched: boolean; dirty: boolean };
}

@Injectable({
  providedIn: 'root',
})
export class MzicArtistCreationStateService {
  private artistImageSource = new BehaviorSubject<string | null>(null);
  public artistImage$ = this.artistImageSource.asObservable();

  private imageIdSource = new BehaviorSubject<string | null>(null);
  public imageId$ = this.imageIdSource.asObservable();

  private artistFormDataSource = new BehaviorSubject<ArtistCreationFormData>(
    {},
  );
  public artistFormData$ = this.artistFormDataSource.asObservable();

  private currentStepSource = new BehaviorSubject<string>('initial');
  public currentStep$ = this.currentStepSource.asObservable();

  private controlStatesSource = new BehaviorSubject<ArtistFormControlStates>(
    {},
  );
  public controlStates$ = this.controlStatesSource.asObservable();

  constructor() {
    this.initStateFromStorage();
  }

  private initStateFromStorage(): void {
    try {
      const savedImage = localStorage.getItem('artist_creation_image');
      const savedImageId = localStorage.getItem('artist_creation_image_id');
      const savedFormData = localStorage.getItem('artist_creation_form_data');
      const savedStep = localStorage.getItem('artist_creation_step');
      const savedControlStates = localStorage.getItem(
        'artist_creation_control_states',
      );

      if (savedImage) {
        this.artistImageSource.next(savedImage);
      }

      if (savedImageId) {
        this.imageIdSource.next(savedImageId);
      }

      if (savedFormData) {
        this.artistFormDataSource.next(JSON.parse(savedFormData));
      }

      if (savedStep) {
        this.currentStepSource.next(savedStep);
      }

      if (savedControlStates) {
        this.controlStatesSource.next(JSON.parse(savedControlStates));
      }
    } catch (error) {
      console.error('Error recovering state from localStorage', error);
      this.clearState();
    }
  }

  setArtistImage(imageUrl: string): void {
    this.artistImageSource.next(imageUrl);
    localStorage.setItem('artist_creation_image', imageUrl);
  }

  setImageId(imageId: string): void {
    this.imageIdSource.next(imageId);
    localStorage.setItem('artist_creation_image_id', imageId);
  }

  getArtistImage(): string | null {
    return this.artistImageSource.value;
  }

  getImageId(): string | null {
    return this.imageIdSource.value;
  }

  updateArtistFormData(data: Partial<ArtistCreationFormData>): void {
    const currentData = this.artistFormDataSource.value;
    const updatedData = { ...currentData, ...data };
    this.artistFormDataSource.next(updatedData);
    localStorage.setItem(
      'artist_creation_form_data',
      JSON.stringify(updatedData),
    );
  }

  getArtistFormData(): ArtistCreationFormData {
    return this.artistFormDataSource.value;
  }

  setCurrentStep(step: string): void {
    this.currentStepSource.next(step);
    localStorage.setItem('artist_creation_step', step);
  }

  getCurrentStep(): string {
    return this.currentStepSource.value;
  }

  updateControlStates(states: Partial<ArtistFormControlStates>): void {
    const currentStates = this.controlStatesSource.value;
    const updatedStates = { ...currentStates, ...states };
    this.controlStatesSource.next(updatedStates);
    localStorage.setItem(
      'artist_creation_control_states',
      JSON.stringify(updatedStates),
    );
  }

  getControlStates(): ArtistFormControlStates {
    return this.controlStatesSource.value;
  }

  clearState(): void {
    this.artistImageSource.next(null);
    this.imageIdSource.next(null);
    this.artistFormDataSource.next({});
    this.currentStepSource.next('initial');
    this.controlStatesSource.next({});
    localStorage.removeItem('artist_creation_image');
    localStorage.removeItem('artist_creation_image_id');
    localStorage.removeItem('artist_creation_form_data');
    localStorage.removeItem('artist_creation_step');
    localStorage.removeItem('artist_creation_control_states');
  }

  /**
   * Clears all registration data when going back from profile setup (first step)
   * This provides a complete fresh start for the registration process
   */
  clearNameAndImage(): void {
    // Since profile setup is the first step, clear ALL registration data
    // This ensures users get a completely fresh start when going back
    this.clearState();
  }

  hasPersistedData(): boolean {
    const formData = this.getArtistFormData();
    return Object.keys(formData).length > 0 || !!this.getArtistImage();
  }
}
